import { ApiError } from '../api/client.js'

export interface PendingOp {
  /** Ops sharing a key supersede each other while still queued. */
  key: string
  run: () => Promise<unknown>
  /** Undo the optimistic change when the server rejects it for good. */
  revert: () => void
  onError?: (error: ApiError) => void
}

const MAX_BACKOFF_MS = 30_000

/**
 * Applies local changes first and sends them one at a time. Ordering matters
 * more than throughput here: two writes to the same item must not race, and a
 * dropped connection must not lose a tick.
 */
export class PendingQueue {
  count = $state(0)
  failing = $state(false)

  private queue: PendingOp[] = []
  private running = false
  private attempt = 0
  private timer: ReturnType<typeof setTimeout> | null = null

  constructor(private readonly onGiveUp: (error: ApiError, op: PendingOp) => void) {}

  push(op: PendingOp): void {
    const existing = this.queue.findIndex((queued) => queued.key === op.key)
    if (existing !== -1) this.queue.splice(existing, 1)
    this.queue.push(op)
    this.count = this.queue.length
    void this.drain()
  }

  /** Called when the browser comes back online. */
  flush(): void {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    this.attempt = 0
    void this.drain()
  }

  private async drain(): Promise<void> {
    if (this.running) return
    this.running = true
    try {
      while (this.queue.length > 0) {
        const op = this.queue[0] as PendingOp
        try {
          await op.run()
          this.queue.shift()
          this.attempt = 0
          this.failing = false
        } catch (error) {
          if (error instanceof ApiError && error.isPermanent) {
            this.queue.shift()
            op.revert()
            op.onError?.(error)
            this.attempt = 0
            continue
          }
          // Network trouble or a server hiccup: keep the change and retry.
          this.failing = true
          this.attempt++
          const wait =
            error instanceof ApiError && error.isRateLimited && error.retryAfter
              ? error.retryAfter * 1000
              : Math.min(MAX_BACKOFF_MS, 2 ** Math.min(this.attempt, 5) * 500)
          if (error instanceof ApiError && error.isRateLimited && this.attempt > 4) {
            this.queue.shift()
            op.revert()
            this.onGiveUp(error, op)
            continue
          }
          await this.sleep(wait)
        } finally {
          this.count = this.queue.length
        }
      }
    } finally {
      this.running = false
      this.count = this.queue.length
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      this.timer = setTimeout(resolve, ms)
    })
  }
}
